/**
 * Auswahl von Kanälen und Apartments. Wählbar ist nur, was der Server annimmt: ein Kanal muss
 * verbunden, ein Apartment in allen gewählten Kanälen gemappt sein. Der Grund steht dabei, damit
 * „ausgegraut" nie rätselhaft bleibt. Abwählen geht immer: eine bestehende Regel kann einen
 * inzwischen getrennten Kanal tragen, und nur ohne ihn lässt sie sich wieder speichern.
 */
import type { ReactNode } from 'react';
import type { ChannelCloseoutOptions } from '@/shared/api';
import { OtaLogo } from '@/features/properties/OtaLogo';
import { cn } from '@/shared/lib/cn';
import { CLOSEOUT_CHANNELS, channelLabel } from './channelCloseoutPresentation';

export function ChannelCloseoutChannelPicker({
  connected,
  selected,
  disabled,
  onToggle,
}: {
  /** null, solange die Kanalliste lädt: dann wird nichts behauptet. */
  connected: string[] | null;
  selected: string[];
  disabled: boolean;
  onToggle: (code: string) => void;
}) {
  return (
    <PickerGroup legend="Welche Kanäle schließen?">
      {CLOSEOUT_CHANNELS.map((channel) => {
        const available = connected?.includes(channel.code) ?? false;
        const checked = selected.includes(channel.code);
        return (
          <Choice
            key={channel.code}
            checked={checked}
            disabled={disabled || (!available && !checked)}
            onToggle={() => onToggle(channel.code)}
            icon={<OtaLogo channel={channel.code} size={22} />}
            label={channel.label}
            hint={connected ? (available ? 'verbunden' : 'nicht verbunden') : undefined}
          />
        );
      })}
    </PickerGroup>
  );
}

export function ChannelCloseoutApartmentPicker({
  apartments,
  channels,
  selected,
  disabled,
  onToggle,
}: {
  apartments: ChannelCloseoutOptions['apartments'];
  channels: string[];
  selected: string[];
  disabled: boolean;
  onToggle: (propertyId: string) => void;
}) {
  return (
    <PickerGroup legend="Für welche Apartments?">
      {apartments.map((apartment) => {
        const missing = channels.filter((channel) => !apartment.channelCodes.includes(channel));
        const checked = selected.includes(apartment.propertyId);
        return (
          <Choice
            key={apartment.propertyId}
            checked={checked}
            disabled={disabled || (missing.length > 0 && !checked)}
            onToggle={() => onToggle(apartment.propertyId)}
            label={apartment.name}
            hint={
              missing.length > 0
                ? `in ${missing.map(channelLabel).join(' und ')} nicht gemappt`
                : undefined
            }
          />
        );
      })}
    </PickerGroup>
  );
}

function PickerGroup({ legend, children }: { legend: string; children: ReactNode }) {
  return (
    <fieldset>
      <legend className="text-ink-soft mb-2 text-[13px] font-medium">{legend}</legend>
      <div className="grid gap-2 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

function Choice({
  checked,
  disabled,
  onToggle,
  icon,
  label,
  hint,
}: {
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
  icon?: ReactNode;
  label: string;
  hint?: string | undefined;
}) {
  return (
    <label
      className={cn(
        'border-hairline bg-card rounded-control flex min-h-12 items-center gap-2.5 border px-3 py-2 text-sm',
        checked && 'border-navy ring-navy/20 ring-1',
        disabled ? 'opacity-55' : 'hover:border-line cursor-pointer',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        className="accent-forest"
      />
      {icon}
      <span className="min-w-0">
        <span className="text-ink block">{label}</span>
        {hint && <span className="text-ink-soft block text-[11px]">{hint}</span>}
      </span>
    </label>
  );
}
